import { useState, FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info, UserPlus, Loader2, CheckCircle2, Download, Copy } from "lucide-react";
import { useTheme } from "@/components/theme-provider";
import { toast } from "sonner";
import {
  enrolDevice,
  PendingApprovalError,
  type EnrolDeviceResult,
} from "@/services/deviceEnrolmentApi";

/**
 * Public device-enrolment page.
 *
 * Mirrors portal.smplify.com/enrol/device — uses the real
 * production two-step flow:
 *   1. POST /api/v1/device/authenticate  → JWT
 *   2. POST /api/v1/device/terms-of-service?accept=true
 *      with Bearer token → enrollment .mobileconfig URL
 *
 * The page is intentionally standalone (no console chrome) so it
 * can be opened directly on the device being enrolled.
 */
export default function EnrolDevicePage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<EnrolDeviceResult | null>(null);

  const logoSrc =
    theme === "dark"
      ? "/lovable-uploads/smplify-logo-inverted.png"
      : "/lovable-uploads/496f0ef7-a54a-4a74-a39b-0a3c3067bb55.png";

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setSubmitting(true);
    setError(null);
    try {
      const response = await enrolDevice({ email, password });
      setResult(response);
      toast.success("Device authenticated. Open the link to install the profile.");
    } catch (err: any) {
      if (err instanceof PendingApprovalError || err?.code === "PENDING_APPROVAL") {
        toast.message("Awaiting administrator approval.");
        navigate("/portal/pending-approval", { replace: true });
        return;
      }
      const raw = err?.message ?? "";
      const message =
        raw.includes("Failed to fetch") || raw.includes("NetworkError")
          ? "We couldn't reach the enrollment service. Please check your connection or contact your administrator."
          : raw.includes("401")
          ? "Invalid email or password."
          : raw || "Enrollment failed. Please try again.";
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const copyEnrollmentUrl = async () => {
    if (!result?.enrollmentUrl) return;
    try {
      await navigator.clipboard.writeText(result.enrollmentUrl);
      toast.success("Enrollment link copied to clipboard.");
    } catch {
      toast.error("Could not copy link. Please copy it manually.");
    }
  };

  return (
    <main className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md flex flex-col items-center gap-8">
        <div className="flex flex-col items-center gap-3">
          <Link to="/" aria-label="Smplify home">
            <img
              src={logoSrc}
              alt="Smplify"
              className="h-16 sm:h-20 w-auto object-contain"
            />
          </Link>
          <p className="text-sm text-muted-foreground tracking-wide">
            Device Management Portal
          </p>
        </div>

        <section
          aria-labelledby="enrol-heading"
          className="w-full rounded-xl border border-border bg-card shadow-sm p-6 sm:p-8 space-y-6"
        >
          <header className="space-y-1 text-center">
            <h1
              id="enrol-heading"
              className="text-2xl font-semibold tracking-tight"
            >
              Enroll your device
            </h1>
            <p className="text-sm text-muted-foreground">
              Sign in with the credentials your administrator provided
            </p>
          </header>

          {result ? (
            <div className="space-y-4">
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>
                  You're authenticated. Open the link below on the device you
                  want to enroll — it will download the management profile.
                </AlertDescription>
              </Alert>
              <div className="rounded-lg border border-border bg-muted/40 p-4 space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Enrollment URL
                </Label>
                <a
                  href={result.enrollmentUrl}
                  className="block break-all text-sm font-medium text-primary hover:underline"
                >
                  {result.enrollmentUrl}
                </a>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={copyEnrollmentUrl}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Copy link
                </Button>
                <Button asChild type="button">
                  <a href={result.enrollmentUrl}>
                    <Download className="mr-2 h-4 w-4" />
                    Open profile
                  </a>
                </Button>
              </div>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setResult(null);
                  setEmail("");
                  setPassword("");
                }}
              >
                Enroll another device
              </Button>
            </div>
          ) : (
            <form className="space-y-5" onSubmit={handleSubmit} noValidate>
              <div className="space-y-2">
                <Label htmlFor="enrol-email">Email</Label>
                <Input
                  id="enrol-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={submitting}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="enrol-password">Password</Label>
                <Input
                  id="enrol-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                />
              </div>

              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  Your account credentials are provided by your administrator.
                  Contact support if you need assistance accessing your account.
                </AlertDescription>
              </Alert>

              {error && (
                <Alert variant="destructive" role="alert">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={submitting || !email || !password}
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Enrolling…
                  </>
                ) : (
                  <>
                    <UserPlus className="mr-2 h-4 w-4" />
                    Enroll
                  </>
                )}
              </Button>
            </form>
          )}
        </section>

        <p className="text-xs text-muted-foreground text-center">
          By enrolling you agree to our{" "}
          <Link to="/portal/terms" className="underline hover:text-foreground">
            Terms and Conditions
          </Link>
          .
        </p>
      </div>
    </main>
  );
}
